import * as React from 'react';
import axios from 'axios';
import styled from 'styled-components';
import {
  EnhancedAppointment,
  Country,
  BuildJsonType,
} from '@bb/domain/dist/types';

import { Button } from 'components';
import { openCovidSignatureAlert } from 'utils';
import errorReporter from '@app/errorReporter';
import Loading from '@app/react-common/Loading';

import CovidAckTemplateModal from './CovidAckTemplateModal';
import Covid19SignedAckModal from './Covid19SignedAckModal';

type AppointmentSignSettings = {
  covid19Signed?: string;
  template?: string;
};
interface CovidSignRequiredProps {
  appointment: BuildJsonType<EnhancedAppointment>;
  dashboardId?: string;
  retsId?: string;
  seller?: boolean;
  isInternal?: boolean;
  cameFromSignEmail?: boolean;
  updateAppointment: (id: string) => void;
  country?: Country;
}

const CovidSignRequired: React.FC<CovidSignRequiredProps> = ({
  appointment,
  dashboardId,
  retsId,
  country,
  seller,
  isInternal,
  cameFromSignEmail,
  updateAppointment,
}) => {
  const [showAckTemplate, setShowAckTemplate] = React.useState(false);
  const [showSignedAck, setShowSignedAck] = React.useState(false);
  const [ackTemplate, setAckTemplate] = React.useState('');
  const [signed, setSigned] = React.useState(!!appointment.covid19Signed);
  const [isLoading, setIsLoading] = React.useState(true);
  const [appointmentSignSettings, setAppointmentSignSettings] =
    React.useState<AppointmentSignSettings>({});

  React.useEffect(() => {
    if (cameFromSignEmail && signed) {
      openCovidSignatureAlert(!!appointment.covid19Overridden);
    }
  }, []);

  React.useEffect(() => {
    const getTemplate = async () => {
      const { data } = await axios.get<string>(
        isInternal ? 'covid19/settings' : '/external/covid19/settings',
        {
          params: {
            listingId: appointment.listingId,
            type: 'ackTemplate',
            retsId,
            dashboardId,
            country,
          },
        },
      );
      setAckTemplate(data);
      setIsLoading(false);
      if (cameFromSignEmail) setShowAckTemplate(true);
    };
    const getAppointmentSignSettings = async () => {
      const { data } = await axios.get<AppointmentSignSettings>(
        isInternal
          ? `/covid19/appointment/${appointment.id}/settings`
          : `/external/covid19/appointment/${appointment.id}/settings`,
        {
          params: {
            retsId,
            dashboardId,
          },
        },
      );
      if (data) setAppointmentSignSettings(data);
      setIsLoading(false);
    };

    if (!signed) {
      getTemplate();
    } else if (!appointment.covid19Overridden) {
      getAppointmentSignSettings();
    } else {
      setIsLoading(false);
    }
  }, [signed]);

  const openModal = (type: 'ackTemplate' | 'signed') => {
    if (type === 'ackTemplate') setShowAckTemplate(true);
    else if (type === 'signed') setShowSignedAck(true);
  };
  const cancel = (type: 'ackTemplate' | 'signed') => {
    if (type === 'ackTemplate') setShowAckTemplate(false);
    else if (type === 'signed') setShowSignedAck(false);
  };

  const submit = async (signature: string) => {
    setIsLoading(true);
    try {
      await axios.post(
        isInternal
          ? `/covid19/appointment/${appointment.id}/settings`
          : `/external/covid19/appointment/${appointment.id}/settings`,
        {
          appointmentId: appointment.id,
          dashboardId,
          retsId,
          covid19Signed: signature,
          template: ackTemplate,
        },
      );
      setShowAckTemplate(false);
      setSigned(true);
      updateAppointment(appointment.id);
    } catch (err) {
      errorReporter.error(`Error saving signature: ${err}`);
    } finally {
      setIsLoading(false);
    }
  };

  if (seller && !signed) return null;

  if (isLoading) {
    return (
      <div className="col-md-6">
        <Loading size="fa-3x" />
      </div>
    );
  }

  return appointment.covid19RequestedAck && !signed ? (
    <StyledContainerDiv className="col-md-6">
      <br />
      <div>
        <strong style={{ color: 'red' }}>ACTION REQUIRED: </strong>
      </div>
      <p style={{ textTransform: 'none' }}>
        This listing requires you to sign a COVID-19 Acknowledgment Form on
        behalf of you and your clients in order for your showing to be approved.
      </p>
      <button
        onClick={() => openModal('ackTemplate')}
        className="btn btn-warning btn-sm"
        style={{ color: 'black' }}
      >
        SIGN NOW
      </button>
      <CovidAckTemplateModal
        ackTemplate={ackTemplate}
        appointment={appointment}
        country={country}
        onCancel={() => cancel('ackTemplate')}
        onSubmit={submit}
        visible={showAckTemplate}
      />
    </StyledContainerDiv>
  ) : (
    <>
      {appointmentSignSettings?.covid19Signed && (
        <Button
          style={{ padding: '4px 10px', margin: '0 5px' }}
          type="link"
          onClick={() => openModal('signed')}
        >
          View Uploaded Signed Acknowledgment
        </Button>
      )}
      <Covid19SignedAckModal
        appointment={appointment}
        appointmentSignSettings={appointmentSignSettings}
        country={country}
        onCancel={() => cancel('signed')}
        visible={showSignedAck}
      />
    </>
  );
};

const StyledContainerDiv = styled.div`
  padding: 10px;
`;

export default CovidSignRequired;
